import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import type { DateRange } from "react-day-picker";
import { ArrowLeft, CalendarOff, CalendarRange, Clock, CalendarClock } from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";

type BlockType = "single_day" | "date_range" | "time_range" | "datetime_range";

const BlockedTimeForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const isEditing = !!id;

  const blockTypes = [
    { value: "single_day" as BlockType, label: t("blocked.type_single_day"), description: t("blocked.desc_single_day"), icon: CalendarOff },
    { value: "date_range" as BlockType, label: t("blocked.type_date_range"), description: t("blocked.desc_date_range"), icon: CalendarRange },
    { value: "time_range" as BlockType, label: t("blocked.type_time_range"), description: t("blocked.desc_time_range"), icon: Clock },
    { value: "datetime_range" as BlockType, label: t("blocked.type_datetime_range"), description: t("blocked.desc_datetime_range"), icon: CalendarClock },
  ];

  // Read prefill params from query string (set when navigating from calendar)
  const prefillDate = searchParams.get("date");
  const prefillStartTime = searchParams.get("start_time");
  const prefillEndTime = searchParams.get("end_time");

  const getInitialType = (): BlockType => {
    if (prefillStartTime && prefillEndTime) return "time_range";
    return "single_day";
  };

  const getInitialDate = (): Date | undefined => {
    if (prefillDate) return new Date(prefillDate + "T00:00:00");
    return undefined;
  };

  const [type, setType] = useState<BlockType>(getInitialType);
  const [startDate, setStartDate] = useState<Date | undefined>(getInitialDate);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [startTime, setStartTime] = useState(prefillStartTime || "09:00");
  const [endTime, setEndTime] = useState(prefillEndTime || "17:00");
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  useEffect(() => {
    if (!isEditing) return;
    const fetch = async () => {
      const { data } = await supabase.from("blocked_times").select("*").eq("id", id).maybeSingle();
      if (data) {
        setType(data.type as BlockType);
        setActive(data.active);
        if (data.start_time) setStartTime(data.start_time.slice(0, 5));
        if (data.end_time) setEndTime(data.end_time.slice(0, 5));
        const sd = new Date(data.start_date + "T00:00:00");
        if (data.type === "date_range" || data.type === "datetime_range") {
          setDateRange({ from: sd, to: data.end_date ? new Date(data.end_date + "T00:00:00") : undefined });
        } else {
          setStartDate(sd);
        }
      }
      setFetching(false);
    };
    fetch();
  }, [id, isEditing]);

  const showEndDate = type === "date_range" || type === "datetime_range";
  const showTime = type === "time_range" || type === "datetime_range";
  const effectiveStartDate = showEndDate ? dateRange?.from : startDate;
  const effectiveEndDate = showEndDate ? dateRange?.to : undefined;
  const canSave = effectiveStartDate && (showEndDate ? effectiveEndDate : true);

  const handleSave = async () => {
    if (!canSave || !profile?.organisation_id) return;
    setLoading(true);
    try {
      const payload = {
        organisation_id: profile.organisation_id,
        type,
        start_date: format(effectiveStartDate!, "yyyy-MM-dd"),
        end_date: effectiveEndDate ? format(effectiveEndDate, "yyyy-MM-dd") : null,
        start_time: showTime ? startTime : null,
        end_time: showTime ? endTime : null,
        active,
      };

      if (isEditing) {
        const { error } = await supabase.from("blocked_times").update(payload).eq("id", id!);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("blocked_times").insert(payload);
        if (error) throw error;
      }

      toast.success(isEditing ? t("blocked.updated") : t("blocked.created"));
      navigate("/dashboard/blocked");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/blocked">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("blocked.back")}
        </Link>
      </Button>

      <h1 className="text-xl font-bold text-foreground mb-6">
        {isEditing ? t("blocked.edit_title") : t("blocked.block_time")}
      </h1>

      {/* Block Type Selection */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("blocked.block_type")}</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {blockTypes.map((bt) => (
            <button
              key={bt.value}
              onClick={() => { setType(bt.value); setDateRange(undefined); }}
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border p-4 transition-all text-center",
                type === bt.value ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30 hover:bg-muted/50"
              )}
            >
              <div className={cn("flex h-10 w-10 items-center justify-center rounded-lg transition-colors", type === bt.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                <bt.icon className="h-5 w-5" />
              </div>
              <span className={cn("text-sm font-medium", type === bt.value ? "text-foreground" : "text-muted-foreground")}>{bt.label}</span>
              <span className="text-[11px] text-muted-foreground leading-tight">{bt.description}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Date Selection */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{showEndDate ? t("blocked.select_dates") : t("blocked.select_date")}</h2>
        {showEndDate ? (
          <div>
            <Calendar mode="range" selected={dateRange} onSelect={setDateRange} numberOfMonths={2} weekStartsOn={1} className="p-3 pointer-events-auto rounded-xl border border-border w-full"
              classNames={{ months: "flex flex-col sm:flex-row w-full gap-4", month: "space-y-4 flex-1", caption: "flex justify-center pt-1 relative items-center", table: "w-full border-collapse", head_row: "flex w-full", head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center", row: "flex w-full mt-2", cell: "flex-1 text-center text-sm p-0 relative", day: "h-10 w-full p-0 font-normal rounded-md hover:bg-muted aria-selected:opacity-100", day_today: "" }}
            />
            {dateRange?.from && (
              <p className="mt-2 text-sm font-medium text-foreground">
                {format(dateRange.from, "MMM d, yyyy")}{dateRange.to && ` – ${format(dateRange.to, "MMM d, yyyy")}`}
              </p>
            )}
          </div>
        ) : (
          <div>
            <Calendar mode="single" selected={startDate} onSelect={setStartDate} weekStartsOn={1} className="p-3 pointer-events-auto rounded-xl border border-border w-full"
              classNames={{ months: "flex flex-col w-full", month: "space-y-4 w-full", caption: "flex justify-center pt-1 relative items-center", table: "w-full border-collapse", head_row: "flex w-full", head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center", row: "flex w-full mt-2", cell: "flex-1 text-center text-sm p-0 relative", day: "h-10 w-full p-0 font-normal rounded-md hover:bg-muted aria-selected:opacity-100", day_today: "" }}
            />
            {startDate && <p className="mt-2 text-sm font-medium text-foreground">{format(startDate, "EEEE, MMMM d, yyyy")}</p>}
          </div>
        )}
      </section>

      {/* Time Selection */}
      {showTime && (
        <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">{t("blocked.select_time")}</h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 md:w-1/2">
            <div className="space-y-2">
              <Label>{t("blocked.start_time")}</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t("blocked.end_time")}</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
        </section>
      )}

      {/* Summary */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("blocked.summary")}</h2>
        {effectiveStartDate ? (
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("blocked.type")}</span>
              <span className="font-medium text-foreground">{blockTypes.find((b) => b.value === type)?.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{showEndDate ? t("blocked.start_date") : t("blocked.date")}</span>
              <span className="font-medium text-foreground">{format(effectiveStartDate, "MMM d, yyyy")}</span>
            </div>
            {showEndDate && effectiveEndDate && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("blocked.end_date")}</span>
                <span className="font-medium text-foreground">{format(effectiveEndDate, "MMM d, yyyy")}</span>
              </div>
            )}
            {showTime && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("blocked.time")}</span>
                <span className="font-medium text-foreground">{startTime} – {endTime}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">{t("blocked.select_date_hint")}</p>
        )}

        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-4 mb-4">
          <div>
            <p className="text-sm font-medium text-foreground">{t("blocked.status")}</p>
            <p className="text-xs text-muted-foreground">{active ? t("blocked.status_active") : t("blocked.status_inactive")}</p>
          </div>
          <Switch checked={active} onCheckedChange={setActive} />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!canSave || loading}>
            {loading ? t("blocked.saving") : isEditing ? t("blocked.save_changes") : t("blocked.block_time")}
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/dashboard/blocked">{t("blocked.cancel")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlockedTimeForm;